import type { Event, Frame } from '@/types/event';
import type { Identity } from '@/types/identity';
import type { SourceStatus, SourceType } from '@/types/source';

export enum ReportStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    COMPLETED = 'COMPLETED',
    ERROR = 'ERROR',
}

export enum ReportType {
    NORMAL = 'NORMAL',
    PROCEDURE = 'PROCEDURE',
}

export enum ReportSourceProcedureStatus {
    PENDING = 'PENDING',
    PROCESSING = 'PROCESSING',
    ERROR = 'ERROR',
    COMPLETED = 'COMPLETED',
}

export interface ReportPipelineSourceStatus {
    sourceId: string;
    sourceName: string;
    sourceStatus: SourceStatus;
    procedureStatus: ReportSourceProcedureStatus;
}

export interface ReportPipelineStatus {
    type: ReportType;
    status: ReportStatus;
    sources: ReportPipelineSourceStatus[];
}

export interface Report {
    id: string;
    createdAt: number;
    updatedAt: number;
    userId: string;
    name: string;
    description: string | null;
    status: ReportStatus;
    type: ReportType;
    templateId: string | null;
    orderVersion: number;
}

export interface ReportListItem extends Report {
    eventCount: number;
    previewThumbnails: (string | null)[];
}

export interface CreateReportPayload {
    name: string;
    description?: string;
    templateId?: string | null;
}

export interface UpdateReportPayload {
    name: string;
    description?: string;
}

export type ReportExportFormat = 'pdf' | 'docx' | 'json';

export interface ReportQueryParams {
    page?: number;
    size?: number;
    search?: string;
    orderBy?: string;
    status?: ReportStatus;
}

export enum ReportEventType {
    TRACKED_OBJECT = 'TRACKED_OBJECT',
    CUSTOM = 'CUSTOM',
}

export interface ReportEvent {
    id: string;
    eventType: ReportEventType;
    promptId: string;
    promptText: string;
    promptPosition: number;
    description: string | null;
    confidence: number | null;
    videoStartSeconds: number | null;
    videoEndSeconds: number | null;
    thumbnailUrls: string[];
    thumbnailFrames: Frame[];
    createdAt: number;
    updatedAt: number;
    sourceId: string;
    sourceName: string;
    sourceType: SourceType;
    classId: number;
    identityId: string | null;
}

export interface ReportWithEvents extends Report {
    events: ReportEvent[];
    hasDownloadableEvents: boolean;
    totalEventCount: number;
    identities: Identity[];
}

export const MAX_PROMPT_TEXT_LENGTH = 500;

export interface ReportEventThumbnailFrame {
    time: number;
    bbox: [number, number, number, number];
}

export interface ReportEventDraftEdits {
    promptText: string;
    description: string;
    thumbnails: ReportEventThumbnailFrame[];
}

export interface ReportEventDraft extends ReportEventDraftEdits {
    event: Event;
    confidence?: number;
}

export interface CreateReportCustomEventPayload {
    sourceId: string;
    promptText: string;
    description?: string;
    confidence?: number;
    videoStartSeconds: number;
    videoEndSeconds: number;
    thumbnailFrames: ReportEventThumbnailFrame[];
}

export interface AddObjectEventInput {
    type: ReportEventType.TRACKED_OBJECT;
    eventId: string;
    promptText: string;
    description: string;
    thumbnailFrames: ReportEventThumbnailFrame[];
    confidence?: number;
}

export interface AddCustomEventInput extends CreateReportCustomEventPayload {
    type: ReportEventType.CUSTOM;
}

export type AddReportEventInput = AddObjectEventInput | AddCustomEventInput;

export interface AddReportEventsResult {
    added: number;
    failed: number;
}

export interface UpdateReportEventPayload {
    promptText?: string;
    description?: string;
    thumbnailFrames?: ReportEventThumbnailFrame[];
}

export interface ReportEventOrderReference {
    id: string;
    eventType: ReportEventType;
}

export interface ReorderReportEventPayload {
    source: ReportEventOrderReference;
    target: ReportEventOrderReference;
    placement: 'before' | 'after';
    groupBy: 'none' | 'prompt' | 'source';
    // Omitted while the report is on its saved manual order; otherwise the full
    // order on screen, which the backend applies the move to and persists.
    currentOrder?: ReportEventOrderReference[];
    expectedOrderVersion: number;
}

export interface ReportOrderResponse {
    orderVersion: number;
}

export interface ReorderReportPromptPayload {
    sourcePromptId: string;
    targetPromptId: string;
    placement: 'before' | 'after';
    expectedOrderVersion: number;
}

export interface ReportCustomEvent {
    id: string;
    createdAt: number;
    updatedAt: number;
    sourceId: string;
    sourceName: string | null;
    sourceType: SourceType | null;
    thumbnailUrls: string[];
    description: string | null;
    videoStartSeconds: number;
    videoEndSeconds: number;
}

export interface ReportEventDetail {
    id: string;
    reportId: string;
    promptText: string | null;
    confidence: number | null;
    createdAt: number;
    updatedAt: number;
    type: ReportEventType;
    frames: Frame[];
    thumbnailFrames: Frame[];
    description: string | null;
    classId: number;
    sourceId: string;
    sourceName?: string;
    sourceType?: SourceType;
    thumbnailUrls: string[];
    videoStartSeconds: number | null;
    videoEndSeconds: number | null;
    eventId: string | null;
}
